import React, { useState, useRef, useCallback } from "react";
import { FormattedMessage } from "react-intl";
import { closeCurrentPopin } from "../../Hook/popins";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";
import "./dailyspin.css";

interface Gift {
  id: number;
  label: string;
  color: string;
  popin?: () => void;
}

const GIFTS: Gift[] = [
  { id: 0, label: "dailyspin__premium_free", color: "#f97316" },
  { id: 1, label: "dailyspin__20_premium", color: "#ec4899" },
  { id: 2, label: "dailyspin__200_tokens", color: "#a855f7" },
  { id: 3, label: "dailyspin__25_off", color: "#38bdf8" },
  { id: 4, label: "dailyspin__30_off", color: "#f87171" },
  { id: 5, label: "dailyspin__50_premium", color: "#34d399" },
];

const SEGMENT_ANGLE = 360 / GIFTS.length;

function DailySpin() {
  const [spinning, setSpinning] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [result, setResult] = useState<Gift | null>(null);
  const wheelRef = useRef<HTMLDivElement>(null);
  const currentRotation = useRef(0);

  const spin = useCallback(() => {
    if (spinning) return;

    setResult(null);
    setCountdown(3);

    let count = 3;
    const interval = setInterval(() => {
      count--;
      if (count > 0) {
        setCountdown(count);
      } else {
        clearInterval(interval);
        setCountdown(null);

        const winnerIndex = Math.floor(Math.random() * GIFTS.length);
        const winner = GIFTS[winnerIndex];

        // The pointer is at top (0°). Conic gradient segment i spans [i*60, (i+1)*60].
        // When wheel is rotated by R degrees, the point originally at R° moves to the top.
        // So we need R % 360 === segmentMid to land that segment under the pointer.
        const extraSpins = 5 * 360;
        let delta =  360 - winnerIndex * SEGMENT_ANGLE - SEGMENT_ANGLE / 2;
        if (delta < 0) delta += 360;
        const targetRotation = currentRotation.current + extraSpins + delta;

        currentRotation.current = targetRotation;
        setSpinning(true);

        if (wheelRef.current) {
          wheelRef.current.style.transition =
            "transform 4s cubic-bezier(0.17, 0.67, 0.12, 0.99)";
          wheelRef.current.style.transform = `rotate(${targetRotation}deg)`;
        }

        setTimeout(() => {
          setSpinning(false);
          setResult(winner);
        }, 4200);
      }
    }, 1000);
  }, [spinning]);

  const handleClaimPrize = () => {
    if (result?.popin) {
      result.popin();
    }
    closeCurrentPopin();
  };

  return (
    <>
      <div
        onClick={closeCurrentPopin}
        className="top-0 left-0 right-0 overflow-hidden bottom-0 fixed bg-black opacity-60 z-40"
      />
      <div className="z-50 p-6 lg:max-w-md w-screen fixed max-h-[100dvh] overflow-y-auto top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 lg:rounded-xl flex flex-col items-center gap-4 bg-gradient-to-b from-[#d946ef] to-[#fb923c]">
        <div
          onClick={closeCurrentPopin}
          className="absolute top-4 right-4 cursor-pointer text-white"
        >
          <FontAwesomeIcon icon={faClose} className="w-5 h-5" />
        </div>

        <h2 className="text-white text-xl font-bold text-center mt-2">
          <FormattedMessage id="dailyspin__title" />
        </h2>
        <p className="text-white text-sm text-center opacity-90">
          <FormattedMessage id="dailyspin__subtitle" />
        </p>

        {/* Wheel container */}
        <div className="relative overflow-hidden w-[280px] h-[280px]">
          {/* Pointer */}
          <div
            className="absolute overflow-hidden -top-3 left-1/2 -translate-x-1/2 z-10 w-0 h-0 drop-shadow-md"
            style={{
              borderLeft: "14px solid transparent",
              borderRight: "14px solid transparent",
              borderTop: "28px solid white",
            }}
          />

          {/* Wheel */}
          <div
            ref={wheelRef}
            className="relative w-full h-full rounded-full overflow-hidden border-[6px] border-white shadow-lg"
          >
            {/* Colored segments via conic gradient */}
            <div
              className="absolute inset-0 rounded-full"
              style={{
                background: `conic-gradient(${GIFTS.map(
                  (g, i) =>
                    `${g.color} ${i * SEGMENT_ANGLE}deg ${(i + 1) * SEGMENT_ANGLE}deg`
                ).join(", ")})`,
              }}
            />

            {/* Labels layer */}
            {GIFTS.map((gift, i) => {
              const angle = i * SEGMENT_ANGLE - SEGMENT_ANGLE;
              return (
                <div
                  key={gift.id}
                  className="absolute top-1/2 left-1/2 w-0 h-0"
                  style={{
                    transform: `rotate(${angle}deg)`,
                  }}
                >
                  <span
                    className="absolute text-[10px] font-extrabold text-white uppercase whitespace-nowrap origin-left"
                    style={{
                      left: "20px",
                      top: "-7px",
                      textShadow: "1px 1px 2px rgba(0,0,0,0.4)",
                    }}
                  >
                    <FormattedMessage id={gift.label} />
                  </span>
                </div>
              );
            })}

            {/* Center circle */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border-4 border-gray-200 z-[5]" />
          </div>

          {/* Countdown overlay */}
          {countdown !== null && (
            <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
              <span
                className="text-[80px] font-black text-white dailyspin-pop"
                style={{ textShadow: "0 0 30px rgba(0,0,0,0.5)" }}
              >
                {countdown}
              </span>
            </div>
          )}
        </div>

        {/* Result display */}
        {result && (
          <div className="bg-white rounded-lg p-3 w-full text-center">
            <p className="text-black font-bold text-lg">
              🎉 <FormattedMessage id={result.label} /> 🎉
            </p>
            <button
              onClick={handleClaimPrize}
              className="mt-2 bg-[#d946ef] text-white font-bold py-2 px-6 rounded-full"
            >
              <FormattedMessage id="dailyspin__claim" />
            </button>
          </div>
        )}

        {/* Spin button */}
        {!result && (
          <button
            onClick={spin}
            disabled={spinning || countdown !== null}
            className={`bg-white text-[#d946ef] font-extrabold text-lg uppercase py-3.5 px-12 rounded-full tracking-wide ${
              spinning || countdown !== null
                ? "opacity-50 cursor-not-allowed"
                : "dailyspin-pulse"
            }`}
          >
            <FormattedMessage id="dailyspin__spin" />
          </button>
        )}
      </div>
    </>
  );
}

export default DailySpin;
